import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Toolbar,
  Typography,
  IconButton,
  Chip,
  Autocomplete,
  Alert,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  AttachFile as AttachFileIcon
} from '@mui/icons-material';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useAuth } from '../../context/AuthContext';
import { formatCustomerName } from '../../utils/formatCustomerName';

export default function ComposeEmail() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const replyToId = searchParams.get('replyTo');
  const { user } = useAuth();
  const isAdmin = user?.ruolo === 'admin';

  const [accountId, setAccountId] = useState('');
  const [to, setTo] = useState([]);
  const [cc, setCc] = useState([]);
  const [bcc, setBcc] = useState([]);
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [error, setError] = useState('');

  // Query accounts
  const { data: accounts = [] } = useQuery({
    queryKey: ['email-accounts'],
    queryFn: () => api.get('/email-accounts').then(res => res.data)
  });

  // Query customers per autocomplete
  const { data: customers = [] } = useQuery({
    queryKey: ['customers'],
    queryFn: () => api.get('/customers', { params: { limit: 1000 } }).then(res => res.data.customers || [])
  });

  // Query email originale se reply
  const { data: originalMessage } = useQuery({
    queryKey: ['email', replyToId],
    queryFn: () => api.get(`/emails/${replyToId}`).then(res => res.data),
    enabled: !!replyToId
  });

  // Send mutation
  const sendMutation = useMutation({
    mutationFn: (data) => api.post('/emails/send', data),
    onSuccess: () => {
      navigate('/crm/email/inbox');
    },
    onError: (err) => {
      setError(err.response?.data?.error || err.message);
    }
  });

  // Popola campi se reply
  useEffect(() => {
    if (originalMessage && replyToId) {
      setAccountId(originalMessage.account_id);
      setTo([originalMessage.from_address]);
      setSubject(`Re: ${originalMessage.subject}`);
      
      const quotedText = `
        <br><br>
        <hr>
        <p><strong>Il ${new Date(originalMessage.date_sent).toLocaleString('it-IT')} ${originalMessage.from_address} ha scritto:</strong></p>
        <blockquote style="border-left: 3px solid #ccc; padding-left: 10px; color: #666;">
          ${originalMessage.body_html || originalMessage.body_text || ''}
        </blockquote>
      `;
      setBodyHtml(quotedText);
    }
  }, [originalMessage, replyToId]);

  // Auto-select primo account
  useEffect(() => {
    if (accounts.length > 0 && !accountId) {
      setAccountId(accounts[0].id);
    }
  }, [accounts, accountId]);

  const handleSend = () => {
    if (accounts.length === 0) {
      setError(isAdmin
        ? 'Nessun account email configurato: crea prima un account in Email → Account.'
        : "Nessun account email configurato: chiedi a un amministratore di aggiungerlo.");
      return;
    }
    if (!accountId || to.length === 0 || !subject) {
      setError('Account, destinatari e oggetto sono obbligatori');
      return;
    }

    sendMutation.mutate({
      account_id: accountId,
      to,
      cc: cc.length > 0 ? cc : undefined,
      bcc: bcc.length > 0 ? bcc : undefined,
      subject,
      body_html: bodyHtml,
      body_text: bodyHtml.replace(/<[^>]*>/g, '') // strip HTML basic
    });
  };

  const customerEmails = customers
    .filter(c => c.email)
    .map(c => ({ label: `${formatCustomerName(c, c.nome)} <${c.email}>`, value: c.email }));

  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      <Paper>
        <Toolbar sx={{ flexWrap: isMobile ? 'wrap' : 'nowrap', gap: 1 }}>
          <IconButton edge="start" onClick={() => navigate('/crm/email/inbox')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, ml: isMobile ? 0 : 2, fontSize: isMobile ? '1rem' : undefined }}>
            {replyToId ? 'Rispondi' : 'Nuova Email'}
          </Typography>
          {!isMobile && <Button
            variant="outlined"
            sx={{ mr: 1 }}
            onClick={() => navigate('/crm/email/accounts')}
          >
            Account
          </Button>}
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={handleSend}
            disabled={sendMutation.isLoading}
            size={isMobile ? 'small' : 'medium'}
          >
            {sendMutation.isLoading ? <CircularProgress size={24} /> : 'Invia'}
          </Button>
        </Toolbar>

        <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {accounts.length === 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {isAdmin
                ? 'Nessun account email configurato. Vai in Email → Account e crea il tuo account IMAP/SMTP.'
                : "Nessun account email configurato. Chiedi a un amministratore di crearne uno."}
            </Alert>
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Account Email</InputLabel>
            <Select
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              label="Account Email"
            >
              {accounts.map(acc => (
                <MenuItem key={acc.id} value={acc.id}>
                  {acc.name} ({acc.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Autocomplete
            multiple
            freeSolo
            options={customerEmails}
            value={to}
            onChange={(e, newValue) => setTo(newValue.map(v => typeof v === 'string' ? v : v.value))}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip label={option} {...getTagProps({ index })} />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="A" placeholder="Aggiungi destinatari" />
            )}
            sx={{ mb: 2 }}
          />

          <Autocomplete
            multiple
            freeSolo
            options={customerEmails}
            value={cc}
            onChange={(e, newValue) => setCc(newValue.map(v => typeof v === 'string' ? v : v.value))}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip label={option} {...getTagProps({ index })} />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Cc" placeholder="Copia conoscenza" />
            )}
            sx={{ mb: 2 }}
          />

          <Autocomplete
            multiple
            freeSolo
            options={customerEmails}
            value={bcc}
            onChange={(e, newValue) => setBcc(newValue.map(v => typeof v === 'string' ? v : v.value))}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip label={option} {...getTagProps({ index })} />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Bcc" placeholder="Copia nascosta" />
            )}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Oggetto"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            sx={{ mb: 2 }}
          />

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Messaggio
            </Typography>
            <ReactQuill
              value={bodyHtml}
              onChange={setBodyHtml}
              theme="snow"
              style={{ height: isMobile ? 200 : 400, marginBottom: isMobile ? 40 : 60 }}
              modules={{
                toolbar: [
                  [{ header: [1, 2, 3, false] }],
                  ['bold', 'italic', 'underline', 'strike'],
                  [{ list: 'ordered' }, { list: 'bullet' }],
                  [{ color: [] }, { background: [] }],
                  ['link'],
                  ['clean']
                ]
              }}
            />
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
